import React, {useEffect, useRef, useState} from "react";
import {NavLink, useNavigate} from "react-router-dom";
import CommissionType from "../../../../model/CommissionType";
import ContentBox from "../../../../components/content-box/ContentBox";
import Modal from "../../../../components/modal/Modal";
import PageActions from "../../../base/PageActions";
import _http_service from "../../../../services/HttpService";
import _image_service from "../../../../services/ImageService";
import _html_parser from "../../../../services/HtmlService";
import _naming_service from "../../../../services/NamingService";
import '../../../styles/Common.scss';
import './CommissionsPage.scss';

interface CommissionsPageProps {
}

interface CommissionPageResultInfo {
    status: string,
    formLink: string,
    queueLink: string,
    offerHtml: string
}

const CommissionsPage = (props: CommissionsPageProps) => {

    const page: PageActions = new PageActions({
        title: `Commissions - Chase the Fox`,
        description: `Check out commission options and status for Chase's art!`,
        keywords: ["commission", "art commission", "cartoon commission", "furry commission", "cute art", "commission status"],
        imgSrc: `chase/commissions/shaded/image.png`,
        url: `commissions`
    });
    
    const [hasFetchedInfo, setFetchInfo] = useState<boolean>(false);
    const [hasFetchedTypes, setFetchTypes] = useState<boolean>(false);

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [selectedExample, setSelectedExample] = useState<string>('');

    const navigateTo = useNavigate();

    const resultInfo = useRef<CommissionPageResultInfo>();
    const resultTypes = useRef<CommissionType[]>();

    useEffect(() => {
        _http_service.getCommissionInfo()
            .then(response => {
                if (!response || Object.keys(response).length === 0) {
                    resultInfo.current = undefined;
                } else {
                    resultInfo.current = {
                        status:  response['status'],
                        formLink: response['form'],
                        queueLink: response['queue'],
                        offerHtml: response['offer']
                    };
                }
            })
            .catch(() => navigateTo("/error", {replace: false}))
            .finally(() => setFetchInfo(true));

        _http_service.getCommissionTypes()
            .then(response => {
                if (!response || response.length === 0) {
                    resultTypes.current = undefined;
                } else {
                    resultTypes.current = response;
                }
            })
            .catch(() => navigateTo("/error", {replace: false}))
            .finally(() => setFetchTypes(true));
    }, []);

    const getStatus = (): string => {
        return (hasFetchedInfo && resultInfo.current && resultInfo.current.status) ? resultInfo.current?.status : '';
    }

    const getFormLink = (): string => {
        return (hasFetchedInfo && resultInfo.current && resultInfo.current.formLink)
            ? resultInfo.current?.formLink : '';
    }

    const getQueueLink = (): string => {
        return (hasFetchedInfo && resultInfo.current && resultInfo.current.queueLink)
            ? resultInfo.current?.queueLink : '';
    }

    const getOfferHtml = (): string => {
        return (hasFetchedInfo && resultInfo.current && resultInfo.current.offerHtml)
            ? resultInfo.current?.offerHtml : '';
    }

    const displayExampleCommission = (exampleImage: string) => {
        setSelectedExample(exampleImage);
        setModalIsOpen(true);
    }

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="commission-modal-view">
                    <div className="commission-modal-header">
                        <h2 className="center headline">{`Example ${selectedExample} commission`}</h2>
                    </div>
                    <img src={_image_service.getCdnImageSrc(`chase/commissions/${selectedExample}/image.png`,
                              'desktop')}
                         alt={`Example ${selectedExample} commission`}
                         title={`Example ${selectedExample} commission`}/>
                </div>
            </Modal>
            <ContentBox className="content world-content"
                        header="Commission Info">
                <h3 className="headline">
                    Status: {getStatus()}
                </h3>
                {(getStatus() && getStatus().trim().toLowerCase() !== 'closed')
                    ? <h3 className="headline">
                        <a href={getFormLink()} target="_blank" rel="noopener noreferrer">
                            If you would like a commission, please click here and fill out the form!
                        </a>
                    </h3>
                    : <></>
                }
                <p className="center">
                    <a href={getQueueLink()} target="_blank" rel="noopener noreferrer">
                        Commission Queue</a> | <NavLink to="/galleries/commissions" target="_blank" rel="noopener noreferrer">
                        Commissions Gallery</NavLink> | <NavLink to="tos" target="_blank" rel="noopener noreferrer">
                        Terms of Service</NavLink>
                </p>
                <p className="center">
                    <strong>Current offer:</strong> <span>{getOfferHtml()}</span>
                </p>
                <p className="center">
                    For any questions regarding commissions, please see my <NavLink to="/contact">Contact</NavLink> page.
                </p>
                <hr/>
                <ul className="commission-type-list">
                    {hasFetchedTypes && resultTypes.current?.map(type => (
                        <li key={`commission-${type.exampleImage}`}>
                            <div className="commission-example-image">
                                <a onClick={() => displayExampleCommission(type.exampleImage)}>
                                    <img
                                        className="commission-image"
                                        src={_image_service.getCdnImageSrc(`chase/commissions/${type.exampleImage}/image.png`)}
                                        alt={`${_naming_service.capitalizeName(type.name)} Example`}
                                    />
                                </a>
                            </div>
                            <p className="commissions-text">
                                <strong style={{fontSize: '24pt'}}>{type.name}: {type.price}</strong><br/>
                                <span>{_html_parser.parse(type.description)}</span>
                            </p>
                            <hr/>
                        </li>
                    ))}
                </ul>
            </ContentBox>
        </>
    );
};

export default CommissionsPage;